import {
  APIFieldItem,
  FieldDataSource,
  SettingsFieldType
} from "@/pages/settings/types";
import {
  DealQuestionnaireQuestionSettings,
  CreateQuestionnaireQuestionSettings
} from "@/pages/deals/types";
import { filter, isEqual } from "lodash";
import axios from "axios";
import i18n from "i18next";
import { DreamerFlow, PublishAutofillQuestionData } from "./hooks/useAutofillApi";
import { SystemFieldReplacement } from "./hooks/useSystemFieldReplacement";

const TRANSLATION_PREFIX = "settings.deals.workflow.workflowDetailedView";

export const getWorkflowTypeDisplay = (
  type: string | null | undefined,
  t: (key: string) => string
): string => {
  if (!type || typeof type !== "string") {
    return t(`${TRANSLATION_PREFIX}.workflowTypes.unknown`);
  }

  const translationKey = `${TRANSLATION_PREFIX}.workflowTypes.${type.toLowerCase()}`;

  if (i18n.exists(translationKey)) {
    return t(translationKey);
  }

  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
};

/**
 * Whether a hardcoded connector fills the question, rather than its own prompt.
 *
 * Narrower than `isSystem`, which marks every out-of-the-box question — most of them have
 * no connector and are filled only through their `dreamerFlow`. When the server does not
 * report the flag, `isSystem` stands in for it.
 */
export const questionHasAutofillConnector = (
  question: DealQuestionnaireQuestionSettings
): boolean => question.hasAutofillConnector ?? question.isSystem ?? false;

/**
 * The question's saved prompt, or null when it has none.
 *
 * An out-of-the-box question can store a placeholder flow that only marks it as filled by
 * Dreamer and carries no prompt, so a flow without instructions is not a saved prompt.
 */
export const savedDreamerFlow = (
  question: DealQuestionnaireQuestionSettings
): DreamerFlow | null => {
  const flow = question.dreamerFlow;
  return flow && typeof flow.instructions === "string" && flow.instructions.trim()
    ? flow
    : null;
};

export interface WorkflowFieldEdits {
  label: string;
  helpText: string | null | undefined;
  required: boolean;
  filledByDreamer: boolean;
  /** The prompt the autofill section reports, or null when it reports none. */
  instructions: string | null;
  /** The flow the autofill section reports, or null when it reports none. */
  dreamerFlow?: DreamerFlow | null;
  config?: unknown;
  savedConfig?: unknown;
}

/**
 * The parts of a flow that change how Dreamer fills the field: which activities run it,
 * which sibling answers it reads, and the confidence handed to the extractor.
 */
const flowBehaviour = (flow: DreamerFlow | null | undefined) => ({
  triggeringActivityTypes: [...(flow?.triggeringActivityTypes ?? [])].sort(),
  dependentFieldKeys: [...(flow?.dependentFieldKeys ?? [])].sort(),
  confidence: flow?.assessment?.confidence ?? null
});

/**
 * Whether the drawer holds anything a save would change on the stored field.
 *
 * A prompt or flow the section does not report (a connector-filled field, or autofill
 * switched off) is not an edit — switching autofill itself is compared through
 * `filledByDreamer`. A regenerated flow is an edit only where it changes how the field
 * is filled, not where only its descriptive metadata differs.
 */
export const workflowFieldHasEdits = (
  field: APIFieldItem,
  edits: WorkflowFieldEdits
): boolean =>
  edits.label.trim() !== (field.label ?? "").trim() ||
  (edits.helpText ?? "").trim() !== (field.helpText ?? "").trim() ||
  edits.required !== (field.required ?? false) ||
  edits.filledByDreamer !== (field.filledByDreamer ?? false) ||
  (edits.instructions !== null &&
    edits.instructions !== (field.dreamerFlow?.instructions ?? null)) ||
  (edits.dreamerFlow != null &&
    !isEqual(
      flowBehaviour(edits.dreamerFlow),
      flowBehaviour(field.dreamerFlow)
    )) ||
  !isEqual(edits.config, edits.savedConfig);

export const transformQuestionsToFields = (
  questions: DealQuestionnaireQuestionSettings[]
): APIFieldItem[] => {
  const filteredQuestions = filter(
    questions,
    (ques) => !ques.isSuccessCriteria
  );
  return filteredQuestions.map((question, index) => {
    const dreamerFlow = savedDreamerFlow(question);
    const hasAutofillConnector = questionHasAutofillConnector(question);
    // Dreamer can only fill a field from a saved prompt or a connector.
    const dreamerFills =
      Boolean(question.filledByDreamer) &&
      (dreamerFlow !== null || hasAutofillConnector);
    return {
      id: question.id,
      fieldKey: question.fieldName || question.id,
      label: question.questionText,
      displayType: (question.responseType.type ||
        "text") as APIFieldItem["displayType"],
      fieldType: SettingsFieldType.CUSTOM,
      dataSource: dreamerFills
        ? FieldDataSource.HYBRID
        : FieldDataSource.MANUAL,
      order: question.questionOrder || index + 1,
      filledByDreamer: question.filledByDreamer ?? false,
      dreamerFlow,
      showInCreation: false,
      showInDetailedView: true,
      required: question.isMandatory,
      config: question.responseType,
      helpText: question.tooltipText,
      section: question.section,
      isSystem: question.isSystem ?? false,
      hasAutofillConnector,
      fieldManagement: {
        canEditDefinition: true,
        canEditOptions: true,
        canDelete: !(question.isSystem ?? false),
        canHide: false
      }
    };
  });
};

export const mapFieldDataToCreateQuestion = (
  fieldData: any,
  questionnaireId: string,
  questionsLength: number = 0
): CreateQuestionnaireQuestionSettings => {
  const nextOrder = questionsLength + 1;

  // Convert options array to the format expected by SingleSelectQuestion
  const formatOptions = (
    options: string[] | undefined
  ): { id: string; displayName: string }[] | undefined => {
    if (!options || !Array.isArray(options)) return undefined;

    return options
      .filter((option) => option && option.trim() !== "")
      .map((option) => ({
        id: option.trim(),
        displayName: option.trim()
      }));
  };

  return {
    questionnaireId: questionnaireId || "",
    fieldName: fieldData.fieldLabel?.toLowerCase().replace(/\s+/g, "_") || null,
    questionText: fieldData.fieldLabel || "",
    responseType: {
      value: fieldData.defaultValue || "none",
      type: fieldData.fieldType || "text",
      setValueType: null,
      unit: null,
      placeholder: fieldData.placeholder || undefined,
      options: formatOptions(fieldData.options),
      textAreaQuestion: fieldData.textAreaQuestion || undefined,
      // Additional properties for new field types
      minValue: fieldData.minValue || undefined,
      maxValue: fieldData.maxValue || undefined,
      option1: fieldData.option1 || undefined,
      option2: fieldData.option2 || undefined,
      allowUsersToAdd: fieldData.allowUsersToAdd || undefined,
      checkboxInlineText: fieldData.checkboxInlineText || undefined
    },
    questionOrder: nextOrder,
    isMandatory: fieldData.isRequiredForPass || fieldData.isRequired || false,
    isSuccessCriteria: false,
    section: null,
    tooltipText: fieldData.fieldHelpText || null,
    isSystem: false,
    relatedQuestionId: null,
    filledByDreamer: false
  };
};

type QuestionResponseType = DealQuestionnaireQuestionSettings["responseType"];
type QuestionOption = NonNullable<QuestionResponseType["options"]>[number];

const optionName = (option: unknown): string => {
  if (typeof option === "string") return option.trim();
  if (option && typeof option === "object") {
    const {
      option: text,
      displayName,
      name
    } = option as Record<string, unknown>;
    return String(text ?? displayName ?? name ?? "").trim();
  }
  return "";
};

/**
 * The field settings a drawer edited (options, switch labels, number bounds, defaults),
 * in the shape a stored question keeps them. Options are stored as `{ id, displayName }`
 * like a newly added field's; an option the question already has keeps its stored id,
 * matched by id first so a renamed option stays the option its answers point at.
 */
const questionSettingsFromConfig = (
  config: Record<string, unknown> | null | undefined,
  storedOptions: QuestionOption[] | undefined
): Partial<QuestionResponseType> => {
  if (!config) return {};
  const { options, ...settings } = config;
  if (!Array.isArray(options)) return settings as Partial<QuestionResponseType>;
  const submitted = options
    .map((option) => ({
      name: optionName(option),
      id: (option as { id?: unknown } | null)?.id
    }))
    .filter(({ name }) => name !== "");
  // Each stored option goes to one submitted option at most: id matches claim first, so a
  // new option reusing a renamed option's former label cannot take that option's id.
  const claimed = new Set<QuestionOption>();
  const claim = (stored: QuestionOption | undefined) => {
    if (stored) claimed.add(stored);
    return stored;
  };
  const byId = submitted.map(({ id }) =>
    claim(
      storedOptions?.find((stored) => !claimed.has(stored) && stored.id === id)
    )
  );
  // Matching by label is only for options without an id; an id that matched nothing is a
  // new row, which must not take back a deleted option of the same label.
  const matched = submitted.map(
    ({ name, id }, index) =>
      byId[index] ??
      claim(
        storedOptions?.find(
          (stored) =>
            !claimed.has(stored) &&
            (id === undefined || stored.id === undefined) &&
            optionName(stored) === name
        )
      )
  );
  const takenIds = new Set<unknown>(storedOptions?.map((stored) => stored.id));
  return {
    ...(settings as Partial<QuestionResponseType>),
    options: submitted.map(({ name }, index) => {
      const stored = matched[index];
      if (stored) return { ...stored, displayName: name };
      let id = name;
      for (let suffix = 2; takenIds.has(id); suffix++) {
        id = `${name}_${suffix}`;
      }
      takenIds.add(id);
      return { id, displayName: name };
    })
  };
};

export const mapFieldDataToUpdateQuestion = (
  fieldData: any,
  existingQuestion: DealQuestionnaireQuestionSettings
): Partial<DealQuestionnaireQuestionSettings> => {
  const settings = questionSettingsFromConfig(
    fieldData.config,
    existingQuestion.responseType.options
  );
  const base: Partial<DealQuestionnaireQuestionSettings> = {
    fieldName:
      fieldData.fieldLabel?.toLowerCase().replace(/\s+/g, "_") ||
      existingQuestion.fieldName,
    questionText: fieldData.fieldLabel || existingQuestion.questionText,
    responseType: {
      ...existingQuestion.responseType,
      ...settings,
      placeholder:
        fieldData.placeholder || existingQuestion.responseType.placeholder,
      options:
        fieldData.options ||
        settings.options ||
        existingQuestion.responseType.options,
      textAreaQuestion:
        fieldData.textAreaQuestion ||
        existingQuestion.responseType.textAreaQuestion
    },
    isMandatory:
      fieldData.isRequiredForPass !== undefined
        ? fieldData.isRequiredForPass
        : existingQuestion.isMandatory,
    isSuccessCriteria: existingQuestion.isSuccessCriteria,
    tooltipText:
      fieldData.fieldHelpText !== undefined
        ? fieldData.fieldHelpText
        : existingQuestion.tooltipText
  };

  if (fieldData.filledByDreamer !== undefined) {
    base.filledByDreamer = fieldData.filledByDreamer;
  }

  return base;
};

/**
 * The question that takes an out-of-the-box field's place once its name is edited.
 *
 * Carries the edited content over the retired field's shape — same response type, section
 * and slot in the workflow — but as an ordinary field: `isSystem` false, so Dreamer fills
 * it from its own prompt instead of a hardcoded connector. `editedFields` is the output of
 * `mapFieldDataToUpdateQuestion`, so an in-place edit and a replacement carry exactly the
 * same edits.
 */
export const buildReplacementQuestion = (
  editedFields: Partial<DealQuestionnaireQuestionSettings>,
  retiredQuestion: DealQuestionnaireQuestionSettings
): CreateQuestionnaireQuestionSettings => ({
  questionnaireId: retiredQuestion.questionnaireId,
  fieldName: editedFields.fieldName ?? null,
  questionText: editedFields.questionText ?? retiredQuestion.questionText,
  responseType: editedFields.responseType ?? retiredQuestion.responseType,
  // Keeps the retired field's section (the server accepts the section name the GET
  // serves and resolves it to the stored id). questionOrder is only honored on the
  // publish (PUT) branch — the plain-create branch's server re-assigns order-last.
  questionOrder: retiredQuestion.questionOrder,
  section: retiredQuestion.section,
  isMandatory: editedFields.isMandatory ?? retiredQuestion.isMandatory,
  isSuccessCriteria: false,
  tooltipText: editedFields.tooltipText ?? null,
  isSystem: false,
  relatedQuestionId: null,
  filledByDreamer: false
});

/** The mutation calls the replacement flow drives, shaped as the hooks' `mutateAsync`. */
export interface SystemQuestionReplacementMutations {
  publishAutofillQuestion: (variables: {
    pathParams: { questionId: string };
    data: PublishAutofillQuestionData;
  }) => Promise<unknown>;
  createQuestion: (variables: {
    data: CreateQuestionnaireQuestionSettings;
  }) => Promise<{ id: string }>;
  deleteQuestion: (variables: {
    pathParams: { questionId: string };
    queryParams: { successorQuestionId: string };
  }) => Promise<unknown>;
  deleteDraftQuestion: (variables: {
    pathParams: { questionId: string };
  }) => Promise<unknown>;
}

/**
 * A missing draft means a prior attempt (or the drawer's own cleanup) already removed
 * it — the goal state holds, so a retry must proceed to the create/retire rather than
 * reject. Anything other than a 404 still aborts the save.
 */
const deleteDraftToleratingMissing = async (
  mutations: SystemQuestionReplacementMutations,
  draftQuestionId: string
): Promise<void> => {
  try {
    await mutations.deleteDraftQuestion({
      pathParams: { questionId: draftQuestionId }
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return;
    }
    throw error;
  }
};

/**
 * Swaps an out-of-the-box question for its edited replacement.
 *
 * Publishes the replacement first — the rename's conversion draft when a prompt was
 * generated, an ordinary create otherwise — and only then retires the original, so a
 * failure part-way leaves the field duplicated rather than gone. The retire names the
 * replacement as successor, which makes the server move the answers deals already gave
 * onto it before the original is retired.
 *
 * `successorCache` makes a retried save reuse the replacement a failed attempt already
 * made live — published or created — instead of making a sibling: the successor's id is
 * recorded before the retire and cleared once it lands, so a retry that finds an entry
 * drops any unpublished conversion draft made since and goes straight to the retire.
 */
export const executeSystemQuestionReplacement = async (
  mutations: SystemQuestionReplacementMutations,
  editedFields: Partial<DealQuestionnaireQuestionSettings>,
  dreamerFlow: DreamerFlow | null,
  retiredQuestion: DealQuestionnaireQuestionSettings,
  replacement: SystemFieldReplacement,
  successorCache?: Map<string, string>
): Promise<void> => {
  const replacementQuestion = buildReplacementQuestion(
    editedFields,
    retiredQuestion
  );

  let successorQuestionId: string;
  const previouslyCreatedId = successorCache?.get(retiredQuestion.id);
  if (previouslyCreatedId) {
    // A prior attempt already created the replacement and failed on the retire. Reuse it —
    // checked before the publish branch, because publishing a draft on the retry would
    // leave that created question live with nothing pointing at it. It keeps the content
    // of the attempt that created it — deleting and recreating with fresher edits is off
    // the table because a retire that failed client-side may still have moved answers onto
    // it. A conversion draft produced since then won't be published either, so drop it —
    // unless it IS the cached successor (a published draft the retire failed after),
    // which deleting would destroy the replacement itself.
    if (
      replacement.draftQuestionId &&
      replacement.draftQuestionId !== previouslyCreatedId
    ) {
      await deleteDraftToleratingMissing(
        mutations,
        replacement.draftQuestionId
      );
    }
    successorQuestionId = previouslyCreatedId;
  } else if (replacement.draftQuestionId && dreamerFlow) {
    await mutations.publishAutofillQuestion({
      pathParams: { questionId: replacement.draftQuestionId },
      data: {
        ...replacementQuestion,
        isDraft: false,
        filledByDreamer: true,
        dreamerFlow
      }
    });
    successorQuestionId = replacement.draftQuestionId;
    // The published draft is now a live question; a retry whose retire fails must find
    // and reuse it just like a created one, whatever state the drawer is in by then.
    successorCache?.set(retiredQuestion.id, successorQuestionId);
  } else {
    // A draft with no flow means autofill was toggled off after a prompt was generated.
    // The draft won't be published, and the drawer's saved-mark disables its own cleanup
    // once the save is handed off — delete it here, before anything is created, so a
    // failure still leaves a cleanly retryable state.
    if (replacement.draftQuestionId) {
      await deleteDraftToleratingMissing(
        mutations,
        replacement.draftQuestionId
      );
    }
    const createdQuestion = await mutations.createQuestion({
      data: replacementQuestion
    });
    successorQuestionId = createdQuestion.id;
    successorCache?.set(retiredQuestion.id, successorQuestionId);
  }

  await mutations.deleteQuestion({
    pathParams: { questionId: retiredQuestion.id },
    queryParams: { successorQuestionId }
  });
  successorCache?.delete(retiredQuestion.id);
};

export interface GroupedFields {
  section: string;
  fields: APIFieldItem[];
}

export const groupFieldsBySection = (
  fields: APIFieldItem[]
): GroupedFields[] => {
  const sectionMap = new Map<string, APIFieldItem[]>();

  const defaultSection = "General";

  const sectionOrder = [
    "authority",
    "access",
    "buy-in",
    "navigator",
    "honestTalk"
  ];

  fields.forEach((field) => {
    const sectionName = field.section || defaultSection;
    if (!sectionMap.has(sectionName)) {
      sectionMap.set(sectionName, []);
    }
    sectionMap.get(sectionName)!.push(field);
  });

  const groupedFields: GroupedFields[] = [];

  const sortedSections = Array.from(sectionMap.keys()).sort((a, b) => {
    if (a === defaultSection) return 1;
    if (b === defaultSection) return -1;

    const indexA = sectionOrder.indexOf(a);
    const indexB = sectionOrder.indexOf(b);

    // If both sections are in the predefined order, sort by that order
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    // If only one is in the predefined order, it comes first
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    // If neither is in the predefined order, sort alphabetically
    return a.localeCompare(b);
  });

  sortedSections.forEach((sectionName) => {
    const sectionFields = sectionMap.get(sectionName)!;
    if (sectionName !== defaultSection || sectionFields.length > 0) {
      groupedFields.push({
        section: sectionName,
        fields: sectionFields.sort((a, b) => a.order - b.order)
      });
    }
  });

  return groupedFields;
};
